import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getOwnedTickers } from '@/lib/bootRequests';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { Button } from '@/components/ui/button';
import LoginModal from '@/components/LoginModal';
import LogoutModal from '@/components/LogoutModal';
import DripExplainerModal from '@/components/DripExplainerModal';
import NotificationBell from '@/components/NotificationBell';
import { COMMUNITY_ENABLED } from '@/config/featureFlags';
import { Briefcase, Star, Sun, Moon, Monitor, LogOut, LogIn, BookOpen, Users, HelpCircle, GraduationCap, Feather, Filter, Crown, ChevronDown, Search, Newspaper } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import FilingAlertsBell from '@/components/FilingAlertsBell';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppHeader() {
  const { preference, setThemePreference } = useMarketTheme();
  const { user, logout, viewAsFree, setViewAsFree, isRealAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/' || location.pathname === '/dashboard';
  // Round 91 — the TwylightCrow landing (`/landing/1`) is brand-locked
  // to dark. We hide the theme toggle there so users don't think the
  // toggle is broken — it's intentional. Other routes (including the
  // public How-To page) keep the toggle and respect the user's choice.
  // Round 91t — also hide on `/` when the user is logged-out, because
  // RootRoute serves Landing1 there too (it's the public start page).
  const isBrandDarkPage = location.pathname.startsWith('/landing/')
    || (location.pathname === '/' && !user);
  // Routes where the page itself renders a <BackOrHomeButton /> for
  // navigation. On those pages the header's "All Stocks" button is
  // redundant + visually noisy — hide it but keep the DOM slot via
  // `invisible pointer-events-none` so the rest of the nav buttons
  // don't shift positions between routes.
  const path = location.pathname;
  // Round 92n — `pageOwnsBackBtn` and the All Stocks back chip it
  // gated are no longer used. The chip was removed in May 2026 to
  // reclaim header space for the Filing Alerts bell. Kept this comment
  // as a breadcrumb in case future work re-introduces the affordance.

  const [ownedCount, setOwnedCount] = useState(0);
  const [watchlistCount, setWatchlistCount] = useState(0);
  const [journalDue, setJournalDue] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [dripModalOpen, setDripModalOpen] = useState(false);

  // Fetch counts
  const fetchCounts = useCallback(() => {
    if (!user) { setOwnedCount(0); setWatchlistCount(0); setJournalDue(false); return; }
    axios.get(`${API}/portfolio`, { withCredentials: true })
      .then(res => setWatchlistCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => {});
    getOwnedTickers()
      .then(data => setOwnedCount(Array.isArray(data) ? data.length : 0))
      .catch(() => {});
    // Round 71l: poll review-status so the Journal button can show a
    // gentle "review due" pulse when the user hasn't done a weekly
    // self-review in 7+ days (or monthly in 30+).
    axios.get(`${API}/journal/review-status`, { withCredentials: true })
      .then(res => setJournalDue(!!(res.data?.weekly?.due || res.data?.monthly?.due)))
      .catch(() => {});
  }, [user]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // Listen for portfolio/watchlist changes from child pages
  useEffect(() => {
    const handler = () => fetchCounts();
    window.addEventListener('portfolio-changed', handler);
    window.addEventListener('watchlist-changed', handler);
    return () => {
      window.removeEventListener('portfolio-changed', handler);
      window.removeEventListener('watchlist-changed', handler);
    };
  }, [fetchCounts]);

  // Determine active view from URL
  const params = new URLSearchParams(location.search);
  const viewParam = params.get('view');
  const isPortfolioActive = isHome && viewParam === 'portfolio';
  const isWatchlistActive = isHome && viewParam === 'watchlist';

  const handlePortfolio = () => {
    if (!user) { setLoginModalOpen(true); return; }
    if (isPortfolioActive) {
      navigate('/');
    } else {
      navigate('/?view=portfolio');
    }
  };

  const handleWatchlist = () => {
    if (!user) { setLoginModalOpen(true); return; }
    if (isWatchlistActive) {
      navigate('/');
    } else {
      navigate('/?view=watchlist');
    }
  };

  const handleJournal = () => {
    // Round 71n followup (Feb 2026): match Watchlist/Portfolio
    // toggle behaviour — clicking the Journal button while already
    // on /journal sends the user back to "/", and the active state
    // un-highlights the moment we leave. Login wall fires on first
    // click for logged-out users so they don't bounce to a gate.
    if (!user) { setLoginModalOpen(true); return; }
    if (location.pathname === '/journal' || location.pathname.startsWith('/journal/')) {
      navigate('/');
    } else {
      navigate('/journal');
    }
  };

  const isJournalActive = location.pathname === '/journal' || location.pathname.startsWith('/journal/');

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-border dark:border-black/70 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-0 sm:h-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Row 1 (mobile) / Left side (desktop): logo only on its own row on mobile.
              Round 96n (Jul 3 2026) — the mobile right-side cluster
              (theme + bell + login) has been moved to its own row
              BELOW the logo so the TwylightCrow banner image is never
              truncated or overlapped. This costs one extra row of
              vertical space on phones but preserves the brand title
              image at full width. Desktop keeps everything on a single
              row. */}
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/dashboard"
              onClick={() => {
                // Round 97c (Jul 2026) — Clicking the DiviDrip logo/wordmark
                // is an explicit "take me home" intent, so wipe the
                // sessionStorage modal-resume cache so the Dashboard
                // doesn't auto-reopen the last stock modal a user just
                // finished looking at. Browser refresh / back button
                // paths still get to resume via sessionStorage as
                // designed — only the deliberate logo click bypasses it.
                try { sessionStorage.removeItem('dividrip:modal-state'); } catch { /* ignore */ }
                // Aug 2026 — Also exit the "Search Stocks" sticky
                // mode. When already on Dashboard, clicking the
                // title is a "reset the view" intent, so the hide-
                // everything-above-search behavior needs to switch
                // off too. Dashboard listens for this event.
                window.dispatchEvent(new CustomEvent('dashboard-exit-search-mode'));
              }}
              className="flex items-center gap-2 sm:gap-3 no-underline min-w-0 sm:shrink-0"
              data-testid="app-header-home"
            >
              <img src="/dividrip-icon.png" alt="DiviDrip" className="w-8 h-8 sm:w-9 sm:h-9 shrink-0" data-testid="app-icon" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold tracking-tight flex items-baseline gap-1.5 whitespace-nowrap" style={{ fontFamily: 'Chivo' }} data-testid="app-title">
                  <span className="dividrip-wordmark shrink-0" data-testid="app-title-text">DiviDrip</span>
                  <span className="text-[10px] sm:text-xs font-normal text-muted-foreground shrink-0">by</span>
                  <img
                    src="/twylight-crow-banner.png"
                    alt="Twylight Crow"
                    className="h-[18px] sm:h-[21px] w-auto object-contain align-baseline shrink-0"
                    data-testid="twylight-crow-banner"
                  />
                </h1>
                <p className="text-xs text-muted-foreground hidden sm:block font-bold">Dividend Stock and ETF Portfolio Tracker</p>
              </div>
            </Link>
            {/* Mobile-only Row 1 right cluster (Jul 2026) — bell + login
                door icons ride shotgun with the brand logo so the top
                line always shows the two most critical actions
                (unread alerts + sign-in/sign-out). Theme toggle drops
                to Row 1b below. Desktop keeps its own bell/login on
                Row 2 (see below) — this cluster hides at sm+. */}
            <div className="flex items-center gap-1 shrink-0 sm:hidden" data-testid="mobile-header-top-cluster">
              <FilingAlertsBell
                user={user}
                idSuffix="-mobile"
                onOpenStock={(t, accession) => {
                  if (!t) return;
                  const ticker = String(t).toUpperCase();
                  if (location.pathname === '/') {
                    window.dispatchEvent(new CustomEvent('open-stock-modal', {
                      detail: { ticker, accession },
                    }));
                    return;
                  }
                  const url = accession
                    ? `/?stock=${ticker}&tab=tenk-8k-risks&accession=${encodeURIComponent(accession)}`
                    : `/?stock=${ticker}`;
                  navigate(url);
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={user ? () => setLogoutModalOpen(true) : () => setLoginModalOpen(true)}
                data-testid={user ? "logout-button-mobile" : "login-button-mobile"}
                className="px-2"
              >
                {user ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          {/* Mobile-only Row 1b: theme toggle only, right-aligned on
              its own line under the logo. Bell + login moved up to
              Row 1 per user request (Jul 2026). */}
          <div className="flex items-center justify-end gap-1 sm:hidden" data-testid="mobile-header-controls-row">
            {!isBrandDarkPage && (
            <div className="flex items-center bg-muted rounded-lg p-0.5" data-testid="theme-toggle-mobile">
              <button onClick={() => setThemePreference('light')} className={`p-1.5 rounded-md transition-colors ${preference === 'light' ? 'bg-background shadow-sm text-orange-500' : 'text-muted-foreground hover:text-foreground'}`} title="Light mode"><Sun className="w-3.5 h-3.5" /></button>
              <button onClick={() => setThemePreference('auto')} className={`p-1.5 rounded-md transition-colors ${preference === 'auto' ? 'bg-background shadow-sm text-cyan-400' : 'text-muted-foreground hover:text-foreground'}`} title="Auto"><Monitor className="w-3.5 h-3.5" /></button>
              <button onClick={() => setThemePreference('dark')} className={`p-1.5 rounded-md transition-colors ${preference === 'dark' ? 'bg-background shadow-sm text-yellow-400' : 'text-muted-foreground hover:text-foreground'}`} title="Dark mode"><Moon className="w-3.5 h-3.5" /></button>
            </div>
            )}
          </div>

          {/* Row 2 (mobile) / Single row (desktop): Portfolio / Watchlist / Journal / Bells / theme / login */}
          <div className="flex items-center justify-center sm:justify-end gap-1 sm:gap-2 flex-wrap">
            {/* Round 92n (May 2026) — "All Stocks" back chip removed.
                Previously it sat invisible-but-spacer on pages that
                own their own back button (Portfolio / Watchlist /
                Journal) to prevent a ~118px layout shift. The user
                doesn't use the chip and the space is better spent
                bringing the Filing Alerts bell up onto the same row
                as Portfolio / Watchlist / Journal. The Snapshot page
                and stock detail pages keep their own back affordances
                so removing the global chip is safe. */}
            <Button
              variant={isPortfolioActive ? 'default' : 'outline'}
              size="sm"
              onClick={handlePortfolio}
              className="gap-1 px-2 sm:gap-1.5 sm:px-3"
              data-testid="portfolio-button"
            >
              <Briefcase className={`w-4 h-4 ${ownedCount > 0 ? '' : 'text-muted-foreground'}`} />
              <span className="text-xs"><span className="hidden sm:inline">My </span>Portfolio</span>
              {ownedCount > 0 && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${isPortfolioActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary text-primary-foreground'}`}>
                  {ownedCount}
                </span>
              )}
            </Button>
            <Button
              variant={isWatchlistActive ? 'default' : 'outline'}
              size="sm"
              onClick={handleWatchlist}
              className="gap-1 px-2 sm:gap-1.5 sm:px-3"
              data-testid="watchlist-button"
            >
              <Star className={`w-4 h-4 ${watchlistCount > 0 ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'}`} />
              <span className="text-xs">Watchlist</span>
              {watchlistCount > 0 && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${isWatchlistActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary text-primary-foreground'}`}>
                  {watchlistCount}
                </span>
              )}
            </Button>
            <Button
              variant={isJournalActive ? 'default' : 'outline'}
              size="sm"
              onClick={handleJournal}
              className="gap-1 px-2 sm:gap-1.5 sm:px-3"
              data-testid="journal-button"
              title={journalDue ? 'Journal — review due' : 'Journal'}
            >
              <BookOpen className={`w-4 h-4 ${journalDue ? 'text-amber-500 animate-pulse' : 'text-muted-foreground'}`} />
              <span className="text-xs">Journal</span>
            </Button>
            {COMMUNITY_ENABLED && (
              <Button
                variant={location.pathname === '/community' ? 'default' : 'outline'}
                size="sm"
                onClick={() => navigate(location.pathname === '/community' ? '/' : '/community')}
                className="gap-1.5"
                data-testid="community-button"
                title="DiviDrip Community"
              >
                <Users className="w-4 h-4 text-violet-500 dark:text-violet-400" />
                <span className="text-xs">Community</span>
              </Button>
            )}
            {/* Desktop theme + login (mobile has them in Row 1 right side) */}
            {!isBrandDarkPage && (
            <div className="hidden sm:flex items-center bg-muted rounded-lg p-0.5" data-testid="theme-toggle">
              <button onClick={() => setThemePreference('light')} className={`p-1.5 rounded-md transition-colors ${preference === 'light' ? 'bg-background shadow-sm text-orange-500' : 'text-muted-foreground hover:text-foreground'}`} data-testid="theme-light-btn" title="Light mode"><Sun className="w-3.5 h-3.5" /></button>
              <button onClick={() => setThemePreference('auto')} className={`p-1.5 rounded-md transition-colors ${preference === 'auto' ? 'bg-background shadow-sm text-cyan-400' : 'text-muted-foreground hover:text-foreground'}`} data-testid="theme-auto-btn" title="Auto"><Monitor className="w-3.5 h-3.5" /></button>
              <button onClick={() => setThemePreference('dark')} className={`p-1.5 rounded-md transition-colors ${preference === 'dark' ? 'bg-background shadow-sm text-yellow-400' : 'text-muted-foreground hover:text-foreground'}`} data-testid="theme-dark-btn" title="Dark mode"><Moon className="w-3.5 h-3.5" /></button>
            </div>
            )}
            {COMMUNITY_ENABLED && <NotificationBell user={user} />}
            {/* Round 92n — SEC 8-K filing alerts bell (Massive 8-K cron).
                Scoped to stocks the user owns OR watchlists; updates
                daily at 6 AM ET. Lives next to the community bell on
                desktop.
                Round 96m (Jul 3 2026) — mobile users get the bell in
                Row 1's right-side cluster (see above). We hide this
                Row 2 instance on mobile so it doesn't render twice. */}
            <div className="hidden sm:inline-flex" data-testid="filing-alerts-bell-desktop-wrap">
            <FilingAlertsBell
              user={user}
              onOpenStock={(t, accession) => {
                // Round 92o++++++++N++++++++ — SEC filing alerts route
                // to the 10K/8K/Risks tab with `accession` triggering a
                // smooth scroll + pulsing fuchsia ring on the matching
                // row. Round 93t — Ex-Div alerts (no accession) open
                // the stock modal at its default tab (Dividend Info).
                if (!t) return;
                const ticker = String(t).toUpperCase();
                const url = accession
                  ? `/?stock=${ticker}&tab=tenk-8k-risks&accession=${encodeURIComponent(accession)}`
                  : `/?stock=${ticker}`;
                navigate(url);
              }}
            />
            </div>
            <Button variant="ghost" size="sm" onClick={user ? () => setLogoutModalOpen(true) : () => setLoginModalOpen(true)} className="hidden sm:inline-flex" data-testid={user ? "logout-button" : "login-button"}>
              {user ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
            </Button>
            {/* Round P111t (Feb 2026) — "View as Free" toggle. Only
                rendered for site-owner admins (`isRealAdmin`). Flips a
                sessionStorage flag that shadows role→'user' for every
                downstream UI gating check so admins can preview the
                Free-tier experience (paywall locks, filter pills, no
                custom watchlists) without logging out. */}
            {isRealAdmin && (
              <Button
                variant={viewAsFree ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewAsFree(!viewAsFree)}
                className="hidden sm:inline-flex gap-1"
                data-testid="view-as-free-toggle"
                title={viewAsFree
                  ? 'Currently previewing the Free-tier experience. Click to switch back to admin view.'
                  : 'You are the site owner (auto-admin). Click to preview what a logged-in Free user sees.'}
              >
                <Crown className={`w-4 h-4 ${viewAsFree ? '' : 'text-yellow-500'}`} />
                <span className="text-xs">{viewAsFree ? 'Free view' : 'Admin'}</span>
              </Button>
            )}
          </div>
        </div>
        {/* Admin previewing as Free — thin banner strip so the toggle
            state is obvious and impossible to forget. */}
        {isRealAdmin && viewAsFree && (
          <div className="bg-yellow-500/15 border-b border-yellow-500/40 text-yellow-800 dark:text-yellow-300 px-3 sm:px-6 py-1 text-[11px] text-center flex items-center justify-center gap-2" data-testid="view-as-free-banner">
            <Crown className="w-3.5 h-3.5" />
            <span>
              Previewing DiviDrip as a <strong>Free-tier</strong> user. Locks and paywalls now render as your real users see them. Backend data still reflects your admin account.
            </span>
            <button
              type="button"
              onClick={() => setViewAsFree(false)}
              className="ml-1 underline font-semibold hover:no-underline"
              data-testid="view-as-free-banner-exit"
            >
              Exit preview
            </button>
          </div>
        )}
        {/* Centered DRIP-explainer link — sits on a second line below
            the main header row so it shows on every page (computer,
            tablet, phone) without crowding the brand or nav buttons. */}
        <div className="border-t border-border/40 dark:border-black/60 px-3 sm:px-6 py-1.5 flex items-center justify-center" data-testid="drip-link-row">
          <button
            onClick={() => setDripModalOpen(true)}
            className="text-xs sm:text-sm font-medium text-sky-700 dark:text-sky-300 hover:underline underline-offset-2 transition-colors inline-flex items-center gap-1"
            data-testid="drip-explainer-link"
          >
            <img
              src="/dividrip-icon2.png"
              alt=""
              aria-hidden="true"
              className="h-3 w-auto sm:h-[14px] object-contain shrink-0"
              data-testid="drip-link-icon"
            />
            DRIP, what it means for your Portfolio.
          </button>
        </div>
        {/* Round 91e — Site-wide "How To" link row.
            Centered amber link under the DRIP explainer. Hidden on
            the brand-locked landing routes which carry their own
            bespoke navigation. Round 92v — Each link self-hides on
            its own page (How To hides on /how-to; Learn hides on
            /learn/*) so visiting either page still leaves a link
            back to the OTHER section in the header — previously the
            How-To page wiped the whole row including the Learn link. */}
        {!isBrandDarkPage && (() => {
          const onHowTo = ['/how-to', '/howto', '/help'].includes(location.pathname);
          const onLearn = location.pathname.startsWith('/learn');
          const onGlossary = location.pathname.startsWith('/glossary');
          // Don't render the bar at all if every link in the row would
          // be hidden — prevents a thin empty divider.
          if (onHowTo && onLearn && onGlossary) return null;
          return (
          <>
          <div
            className="border-t border-border/40 dark:border-black/60 px-3 sm:px-6 py-1.5 flex items-center justify-center gap-4 sm:gap-6 flex-wrap"
            data-testid="how-to-link-row"
          >
            {!onHowTo && (
              <Link
                to="/how-to"
                className="inline-flex items-center gap-1.5 font-semibold transition-colors hover:underline underline-offset-2"
                data-testid="how-to-link"
                title="Open the DiviDrip user guide"
              >
                <HelpCircle
                  className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-amber-700 dark:text-amber-500 shrink-0"
                  strokeWidth={2.4}
                  data-testid="how-to-link-icon"
                />
                <span className="text-sm sm:text-base text-amber-500 dark:text-amber-400">
                  How To
                </span>
              </Link>
            )}
            {!onGlossary && (
              <Link
                to="/glossary"
                className="inline-flex items-center gap-1.5 font-semibold transition-colors hover:underline underline-offset-2"
                data-testid="glossary-link"
                title="Plain-English definitions of every dividend, tax, and metrics term used across DiviDrip"
              >
                <Feather
                  className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-purple-800 dark:text-purple-200 shrink-0"
                  strokeWidth={2.2}
                  data-testid="glossary-link-icon"
                />
                <span className="text-sm sm:text-base text-purple-800 dark:text-purple-200">
                  Glossary
                </span>
              </Link>
            )}
            {!onLearn && (
              <Link
                to="/learn"
                className="inline-flex items-center gap-1.5 font-semibold transition-colors hover:underline underline-offset-2"
                data-testid="learn-link"
                title="Free dividend investing guides"
              >
                <GraduationCap
                  className="w-[21px] h-[21px] sm:w-6 sm:h-6 text-violet-600 dark:text-violet-400 shrink-0"
                  strokeWidth={2.2}
                />
                <span className="text-sm sm:text-base text-violet-500 dark:text-violet-300">
                  Learn
                </span>
              </Link>
            )}
            {/* Feb 2026 — Forced line break on phone view only. This
                w-full flex item pushes the Stocks + Market Movers
                News links onto their own row on <sm screens, giving
                the phone layout the requested two-row shape:
                  Row 1: How To · Glossary · Learn
                  Row 2: Stocks · Market Movers News
                Desktop keeps the whole nav on a single row. */}
            <span className="basis-full h-0 sm:hidden" aria-hidden />
            {/* Feb 2026 — Mobile-only "Stocks" quick-focus button.
                Moved to sit AFTER Learn so on phone view it wraps
                onto the second row alongside Market Movers News,
                leaving How To / Glossary / Learn on the first row.
                Hidden at sm+ (desktop already has "Search Stocks"
                on the row below). */}
            <button
              type="button"
              onClick={() => {
                if (isHome && !viewParam) {
                  window.dispatchEvent(new CustomEvent('dashboard-force-search-mode'));
                } else {
                  navigate('/dashboard?search=1');
                }
              }}
              className="sm:hidden inline-flex items-center gap-1.5 font-semibold transition-colors hover:underline underline-offset-2"
              data-testid="search-stocks-mobile-button"
              title="Focus the leaderboard's stock-search input and hide everything above it"
            >
              <Search
                className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0"
                strokeWidth={2.2}
                data-testid="search-stocks-mobile-icon"
              />
              <span className="text-sm text-emerald-600 dark:text-emerald-400">
                Stocks
              </span>
            </button>
            {/* Feb 2026 — Market Movers News link. Shown on all
                viewports. Desktop wraps into the same row as
                How To / Glossary / Learn; phones wrap it onto the
                second row next to the Stocks button. */}
            {location.pathname !== '/news' && (
              <Link
                to="/news"
                className="inline-flex items-center gap-1.5 font-semibold transition-colors hover:underline underline-offset-2"
                data-testid="news-link"
                title="Live news for today's top dividend and market movers"
              >
                <Newspaper
                  className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-sky-600 dark:text-sky-300 shrink-0"
                  strokeWidth={2.2}
                  data-testid="news-link-icon"
                />
                <span className="text-sm sm:text-base text-sky-600 dark:text-sky-300">
                  Market Movers News
                </span>
              </Link>
            )}
          </div>
          {/* Round P111m (Feb 2026) — Stock Screener + Challenge
              row. Sits UNDER the How To / Glossary / Learn row on
              desktop ONLY. Mobile still uses the Dashboard-toolbar
              copies (Dashboard.js does NOT render these on <sm),
              so phone view stays untouched. */}
          <div
            className="hidden sm:flex items-center justify-center gap-4 sm:gap-6 flex-wrap pt-1"
            data-testid="dashboard-header-secondary-row"
          >
            <Link
              to="/screener"
              className="inline-flex items-center gap-1.5 font-semibold transition-colors hover:underline underline-offset-2"
              data-testid="stock-screener-header-link"
              title="Filter the full universe by yield, sector, frequency, quality, and more"
            >
              <Filter
                className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-blue-700 dark:text-blue-300 shrink-0"
                strokeWidth={2.2}
                data-testid="stock-screener-header-icon"
              />
              <span className="text-sm sm:text-base text-sky-600 dark:text-sky-400">
                Stock Screener
              </span>
            </Link>
            {/* Aug 2026 — "Search Stocks" quick-focus button. Only
                renders on desktop (header row is `hidden sm:flex`).
                Clicking dispatches `dashboard-force-search-mode`
                which the Dashboard listens for and toggles its
                sticky "hide everything above the search box" flag.
                Green tone matches the finished-cron progress bar. */}
            <button
              type="button"
              onClick={() => {
                // Feb 2026 — When user is already on the plain
                // Dashboard leaderboard we can just dispatch the
                // event and the mounted listener switches sticky
                // mode on. From ANY other route (or from the
                // Dashboard's Watchlist / Portfolio sub-views where
                // the search input isn't rendered), hop to
                // `/dashboard?search=1` and let Dashboard's mount
                // effect pick up the flag.
                if (isHome && !viewParam) {
                  window.dispatchEvent(new CustomEvent('dashboard-force-search-mode'));
                } else {
                  navigate('/dashboard?search=1');
                }
              }}
              className="inline-flex items-center gap-1.5 font-semibold transition-colors hover:underline underline-offset-2"
              data-testid="search-stocks-header-button"
              title="Focus the leaderboard's stock-search input and hide everything above it"
            >
              <Search
                className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-emerald-600 dark:text-emerald-400 shrink-0"
                strokeWidth={2.2}
                data-testid="search-stocks-header-icon"
              />
              <span className="text-sm sm:text-base text-emerald-600 dark:text-emerald-400">
                Search Stocks
              </span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 font-semibold transition-colors hover:underline underline-offset-2"
                  data-testid="challenge-header-trigger"
                  title="Kings, Aristocrats, and Achievers dividend challenges"
                >
                  <Crown
                    className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-amber-700 dark:text-amber-500 shrink-0"
                    strokeWidth={2.4}
                    data-testid="challenge-header-icon"
                  />
                  <span className="text-sm sm:text-base text-amber-500 dark:text-amber-400">
                    Challenge
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" data-testid="challenge-header-menu">
                <Link to="/kings-challenge">
                  <DropdownMenuItem className="cursor-pointer gap-2" data-testid="challenge-header-kings-link">
                    <span>👑</span> Kings Challenge
                  </DropdownMenuItem>
                </Link>
                <Link to="/aristocrats-challenge">
                  <DropdownMenuItem className="cursor-pointer gap-2" data-testid="challenge-header-aristocrats-link">
                    <span>🏆</span> Aristocrats Challenge
                  </DropdownMenuItem>
                </Link>
                <Link to="/achievers-challenge">
                  <DropdownMenuItem className="cursor-pointer gap-2" data-testid="challenge-header-achievers-link">
                    <span>🎖️</span> Achievers Challenge
                  </DropdownMenuItem>
                </Link>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          </>
          );
        })()}
      </header>
      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
      <LogoutModal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={async () => {
          try { await logout(); } catch {}
          setLogoutModalOpen(false);
        }}
      />
      <DripExplainerModal open={dripModalOpen} onClose={() => setDripModalOpen(false)} />
    </>
  );
}
